import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * DefaultSEO — settato globalmente in <Router>. Si occupa di:
 *   1. <link rel="canonical"> dinamico per ogni rotta
 *   2. <meta name="robots" content="noindex,nofollow"> su rotte private
 *   3. JSON-LD Organization schema (su tutte le pagine)
 *   4. Copy event listener: appende fonte+copyright al testo copiato
 *
 * Manipolazione diretta del DOM per aggirare bug noto di react-helmet-async
 * 2.x + React 18 StrictMode che impedisce il rendering di link/meta/script
 * children.
 */

const SITE_URL = 'https://www.restainpace.it';
const SITE_NAME = 'Resta in Pace';

// Rotte private che NON devono finire su Google
const NO_INDEX_PREFIXES = [
  '/admin',
  '/dashboard',
  '/dashboard-agenzia',
  '/dashboard-fiorista',
  '/dashboard-blogger',
  '/dashboard-professionista',
  '/login',
  '/login-agenzia',
  '/login-fiorista',
  '/login-blogger',
  '/registrazione',
  '/registrazione-agenzia',
  '/registrazione-fiorista',
  '/registrazione-blogger',
  '/agency-login',
  '/agency-register',
  '/agenzia/dashboard',
  '/agenzia/profilo',
  '/agenzia/sedi',
  '/agenzia/fatturazione',
  '/agenzia/statistiche',
  '/agenzia/manifesti',
  '/agenzia/libro-memoria',
  '/agenzia/fatture',
  '/agenzia/contratti',
  '/agenzia/archivio',
  '/agenzia/integrazioni',
  '/agenzia/impostazioni',
  '/agenzia/api-keys',
  '/area-privata',
  '/area-famiglia',
  '/famiglia/',
  '/florist',
  '/professionista',
  '/blogger',
  '/email-preferences',
  '/email-verification',
  '/onboarding',
  '/password-reset',
  '/repair',
  '/checkout',
  '/donazione/successo',
  '/successo',
];

const isPrivatePath = (pathname) =>
  NO_INDEX_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + '/') || pathname.startsWith(p)
  );

const upsertCanonical = (href) => {
  document.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
  const link = document.createElement('link');
  link.setAttribute('rel', 'canonical');
  link.setAttribute('href', href);
  link.setAttribute('data-seo-managed', 'true');
  document.head.appendChild(link);
};

const upsertRobots = (content) => {
  document.querySelectorAll('meta[name="robots"]').forEach((el) => el.remove());
  if (content) {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'robots');
    meta.setAttribute('content', content);
    meta.setAttribute('data-seo-managed', 'true');
    document.head.appendChild(meta);
  }
};

const upsertOrganizationSchema = () => {
  // Inserisci una sola volta (id univoco)
  const existing = document.getElementById('ld-organization');
  if (existing) return;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    description:
      'Portale italiano dedicato ai necrologi, commemorazioni funebri e servizi per il lutto. Pubblica necrologi, accendi candele virtuali, trova agenzie funebri.',
    address: { '@type': 'PostalAddress', addressCountry: 'IT' },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      url: `${SITE_URL}/contatti`,
      availableLanguage: ['Italian'],
    },
    sameAs: [
      'https://www.instagram.com/restainpace.it/',
      'https://www.facebook.com/profile.php?id=61586631495655',
      'https://www.tiktok.com/@restainpace8',
      'https://www.linkedin.com/in/resta-in-pace-75797940b/',
      'https://open.spotify.com/show/033gzN9cc4YbtABRzKkLgS',
    ],
  };
  const s = document.createElement('script');
  s.type = 'application/ld+json';
  s.id = 'ld-organization';
  s.text = JSON.stringify(schema);
  document.head.appendChild(s);
};

const COPY_SOURCE_NOTE = `\n\nFonte: ${SITE_URL.replace('https://www.', '')} — © ${new Date().getFullYear()} ${SITE_NAME}. Tutti i diritti riservati.`;

const attachCopyListener = () => {
  if (window.__rip_copy_listener_attached) return;
  window.__rip_copy_listener_attached = true;
  document.addEventListener('copy', (event) => {
    try {
      const selection = (document.getSelection() || '').toString();
      // Solo se la selezione e' significativa (>= 40 char di testo continuo) per
      // evitare di "sporcare" copie di indirizzi/date/numeri brevi
      if (!selection || selection.trim().length < 40) return;
      const augmented = selection + COPY_SOURCE_NOTE;
      if (event.clipboardData) {
        event.clipboardData.setData('text/plain', augmented);
        event.preventDefault();
      }
    } catch (e) {
      // Non bloccare mai la copia in caso di errore
    }
  });
};

const DefaultSEO = () => {
  const location = useLocation();
  const pathname = location.pathname || '/';

  useEffect(() => {
    const canonicalUrl = `${SITE_URL}${pathname === '/' ? '' : pathname}`;
    upsertCanonical(canonicalUrl);

    if (isPrivatePath(pathname)) {
      upsertRobots('noindex,nofollow');
    } else {
      upsertRobots(null);
    }
  }, [pathname]);

  useEffect(() => {
    upsertOrganizationSchema();
    attachCopyListener();
  }, []);

  return null;
};

export default DefaultSEO;
